import React, { useEffect, useState } from 'react';
import { AppShell } from '@/components/nav/AppShell';
import { jobsApi, kanbanApi, customersApi, propertiesApi } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { JobStatusPill } from '@/components/jobs/JobStatusPill';
import { Link } from 'react-router-dom';
import { fmtDate } from '@/lib/format';
import { DndContext, useDraggable, useDroppable, closestCorners, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { toast } from 'sonner';

export default function PipelinePage() {
  const [jobs, setJobs] = useState([]);
  const [stages, setStages] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [properties, setProperties] = useState([]);

  // On touch, a 5px pointer threshold turns every horizontal swipe into a drag,
  // so the column strip cannot be scrolled. TouchSensor requires a press-and-hold
  // instead, which leaves plain swipes to the scroll container.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } }),
  );

  const load = async () => {
    const [js, st, cs, ps] = await Promise.all([jobsApi.list(), kanbanApi.list(), customersApi.list(), propertiesApi.list()]);
    setJobs(js);
    setStages(st);
    setCustomers(cs);
    setProperties(ps);
  };
  useEffect(() => { load(); }, []);

  const jobsInStage = (stageId) => (jobs || []).filter(j => j.kanban_stage_id === stageId);
  const unassigned = (jobs || []).filter(j => !j.kanban_stage_id);

  const onDragEnd = async (evt) => {
    const { active, over } = evt;
    if (!over) return;
    const jobId = active.id;
    const stageId = over.id === 'unassigned' ? '' : over.id;
    try {
      if (stageId) {
        await jobsApi.setKanban(jobId, stageId);
      } else {
        await jobsApi.setKanban(jobId, '');
      }
      toast.success('Job moved');
      load();
    } catch { toast.error('Move failed'); }
  };

  return (
    <AppShell title="Pipeline">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between [&>*:last-child]:w-full sm:[&>*:last-child]:w-auto mb-6">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-semibold">Pipeline</h1>
          <p className="text-sm text-muted-foreground">Drag jobs across your custom stages.</p>
        </div>
      </div>
      <DndContext sensors={sensors} collisionDetection={closestCorners} onDragEnd={onDragEnd}>
        {/* 85vw lets the next column peek, so the strip reads as scrollable. */}
        <div className="grid grid-flow-col auto-cols-[85vw] sm:auto-cols-[280px] gap-3 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0">
          <Column id="unassigned" title="Unassigned" jobs={unassigned} customers={customers} properties={properties} />
          {stages.map(s => (
            <Column key={s.id} id={s.id} title={s.name} jobs={jobsInStage(s.id)} customers={customers} properties={properties} />
          ))}
        </div>
      </DndContext>
    </AppShell>
  );
}

function Column({ id, title, jobs, customers, properties }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    // Sunken well, so the cards inside it read as sitting above the column.
    <div ref={setNodeRef} className={`rounded-xl border bg-surface-sunken ${isOver ? 'ring-2 ring-primary' : ''}`}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-border-strong">
        <div className="text-sm font-semibold">{title}</div>
        <div className="text-xs text-muted-foreground">{jobs.length}</div>
      </div>
      <div className="p-2 space-y-2 min-h-[200px]">
        {jobs.map(j => <KanbanCard key={j.id} job={j} customers={customers} properties={properties} />)}
      </div>
    </div>
  );
}

function KanbanCard({ job, customers, properties }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: job.id });
  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : {};
  const c = customers.find(x => x.id === job.customer_id);
  const p = properties.find(x => x.id === job.property_id);
  return (
    <div ref={setNodeRef} style={style} {...listeners} {...attributes} className={`rounded-lg border bg-card p-3 shadow-xs transition-shadow hover:shadow-md cursor-grab active:cursor-grabbing ${isDragging ? 'opacity-80 shadow-lg' : ''}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="font-medium text-sm leading-snug">{p?.nickname || c?.name || 'Job'}</div>
        <JobStatusPill status={job.status} />
      </div>
      <div className="text-xs text-muted-foreground mt-1">{fmtDate(job.scheduled_at)}</div>
      <div className="mt-2">
        <Link to={`/jobs/${job.id}`} className="text-xs text-primary hover:underline">Open</Link>
      </div>
    </div>
  );
}
